import React from 'react';
import { Button, Input, message } from 'antd';
import { DeleteOutlined, PictureOutlined, SendOutlined } from '@ant-design/icons';

const MAX_LOGS = 40;
const MAX_CONTENT_LENGTH = 950;
const LOG_KEY = '__sjFeedbackLogs';
const MAX_IMAGE_COUNT = 4;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const FEEDBACK_SUBMIT_TIMEOUT_MS = 10000;
const FEEDBACK_TIMEOUT_MESSAGE = '反馈提交超时，请稍后重试';
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

function pushFeedbackLog(level, args) {
  if (typeof window === 'undefined') return;

  const logs = window[LOG_KEY] || [];
  const text = args
    .map(item => {
      if (item instanceof Error) return `${item.name}: ${item.message}`;
      if (typeof item === 'string') return item;
      try {
        return JSON.stringify(item);
      } catch (e) {
        return String(item);
      }
    })
    .join(' ');

  window[LOG_KEY] = [
    ...logs.slice(-(MAX_LOGS - 1)),
    {
      level,
      text,
      time: new Date().toISOString(),
      page: window.location.href,
    },
  ];
}

export function installFeedbackLogCollector() {
  if (typeof window === 'undefined' || window.__sjFeedbackCollectorInstalled) return;
  window.__sjFeedbackCollectorInstalled = true;
  window[LOG_KEY] = window[LOG_KEY] || [];

  ['error', 'warn'].forEach(level => {
    const original = console[level];
    console[level] = (...args) => {
      pushFeedbackLog(level, args);
      original.apply(console, args);
    };
  });

  window.addEventListener('error', event => {
    pushFeedbackLog('window-error', [
      event.message,
      event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : '',
    ]);
  });

  window.addEventListener('unhandledrejection', event => {
    pushFeedbackLog('unhandledrejection', [event.reason && event.reason.message ? event.reason.message : event.reason]);
  });
}

function readFeedbackLogs() {
  if (typeof window === 'undefined') return [];
  return window[LOG_KEY] || [];
}

export function buildFeedbackPayload({ issue, contact, user, images = [] }) {
  const logs = readFeedbackLogs();
  const context = {
    page: window.location.href,
    pathname: window.location.pathname,
    hash: window.location.hash,
    title: document.title,
    userNum: user && user.userNum,
    nickname: user && user.nickname,
    appId: window.app && window.app.appId,
    biz: window.app && window.app.biz,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    online: navigator.onLine,
    time: new Date().toISOString(),
  };

  const content = [
    `具体问题：${issue}`,
    images.length ? `图片附件：${images.length} 张` : '',
    '',
    `页面：${context.page}`,
    `用户：${context.nickname || '-'} (${context.userNum || '-'})`,
    `环境：${context.userAgent}`,
    `窗口：${context.viewport}`,
    '',
    '最近问题日志：',
    logs.length ? logs.map(log => `[${log.time}] ${log.level}: ${log.text}`).join('\n') : '无',
  ].join('\n');

  return {
    content:
      content.length > MAX_CONTENT_LENGTH ? `${content.slice(0, MAX_CONTENT_LENGTH - 20)}\n...日志已截断` : content,
    contact,
    suggestion: issue,
    userNum: context.userNum,
    hasLog: logs.length ? 1 : 0,
    hasImg: images.length ? 1 : 0,
    appId: context.appId,
    platform: 'pc',
    clientVersion: 'web',
    bd: '',
  };
}

export function toFeedbackFormData(payload) {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  return formData;
}

export function fetchWithTimeout(url, options = {}, timeoutMs = FEEDBACK_SUBMIT_TIMEOUT_MS) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timeoutId;

  const timeoutPromise = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      if (controller) {
        controller.abort();
      }
      const error = new Error(FEEDBACK_TIMEOUT_MESSAGE);
      error.name = 'FeedbackTimeoutError';
      reject(error);
    }, timeoutMs);
  });

  const fetchOptions = controller ? { ...options, signal: controller.signal } : options;

  return Promise.race([fetch(url, fetchOptions), timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

function createImageItem(file) {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    file,
    name: file.name || `feedback-${Date.now()}.png`,
    previewUrl: URL.createObjectURL(file),
  };
}

export default class FeedbackForm extends React.Component {
  state = {
    submitting: false,
    issue: this.props.initialIssue || '',
    contact: '',
    images: [],
  };

  componentWillUnmount() {
    this.revokeImagePreviews(this.state.images);
  }

  revokeImagePreviews = images => {
    images.forEach(image => {
      if (image.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }
    });
  };

  validateImageFile = file => {
    if (!file || !ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      message.warning('仅支持 PNG、JPG、GIF、WebP 图片');
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      message.warning('单张图片不能超过 5MB');
      return false;
    }
    return true;
  };

  addImageFiles = files => {
    const imageFiles = Array.from(files || []).filter(file => file && file.type && file.type.startsWith('image/'));
    if (!imageFiles.length) return;

    this.setState(prevState => {
      const remain = MAX_IMAGE_COUNT - prevState.images.length;
      if (remain <= 0) {
        message.warning(`最多添加 ${MAX_IMAGE_COUNT} 张图片`);
        return null;
      }

      const accepted = imageFiles.filter(this.validateImageFile).slice(0, remain).map(createImageItem);

      if (imageFiles.length > remain) {
        message.warning(`最多添加 ${MAX_IMAGE_COUNT} 张图片，已自动忽略多余图片`);
      }

      if (!accepted.length) return null;

      return { images: [...prevState.images, ...accepted] };
    });
  };

  handlePaste = event => {
    const items = event.clipboardData && event.clipboardData.items;
    if (!items) return;

    const files = Array.from(items)
      .filter(item => item.kind === 'file' && item.type && item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter(Boolean);

    if (files.length) {
      event.preventDefault();
      this.addImageFiles(files);
      message.success('已添加剪贴板图片');
    }
  };

  handleFileInputChange = event => {
    this.addImageFiles(event.target.files);
    event.target.value = '';
  };

  removeImage = id => {
    this.setState(prevState => {
      const removed = prevState.images.find(image => image.id === id);
      if (removed && removed.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return {
        images: prevState.images.filter(image => image.id !== id),
      };
    });
  };

  submit = async () => {
    const issue = this.state.issue.trim();
    if (!issue) {
      message.warning('请先写下具体问题');
      return;
    }

    const payload = buildFeedbackPayload({
      issue,
      contact: this.state.contact.trim(),
      user: this.props.user || {},
      images: this.state.images,
    });

    this.setState({ submitting: true });
    try {
      const formData = toFeedbackFormData(payload);
      this.state.images.forEach(image => {
        formData.append('files', image.file, image.name);
      });

      const response = await fetchWithTimeout('/api/wx/feedback', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok || !data || data.code !== 0) {
        throw new Error(data && data.msg ? data.msg : `HTTP ${response.status}`);
      }
      message.success('反馈已提交，我们会尽快查看');
      this.revokeImagePreviews(this.state.images);
      this.setState({ issue: '', contact: '', images: [] });
      if (this.props.onSubmitted) {
        this.props.onSubmitted();
      }
    } catch (error) {
      console.error('[feedback] submit failed', {
        error: error && error.message,
        payload,
      });
      message.error(
        error && error.name === 'FeedbackTimeoutError'
          ? FEEDBACK_TIMEOUT_MESSAGE
          : '反馈提交失败，问题内容已保留在控制台日志中'
      );
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const { issue, contact, submitting, images } = this.state;

    return (
      <div onPaste={this.handlePaste}>
        <div className="sj-feedback-field">
          <div className="sj-feedback-label">具体问题</div>
          <Input.TextArea
            value={issue}
            onChange={e => this.setState({ issue: e.target.value })}
            placeholder="请描述你遇到的问题、期望结果和实际结果，可直接粘贴截图"
            autoSize={{ minRows: 4, maxRows: 8 }}
            maxLength={1000}
            showCount
          />
        </div>
        <div className="sj-feedback-field">
          <div className="sj-feedback-label">图片附件</div>
          <input
            ref={ref => {
              this.fileInput = ref;
            }}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            multiple
            className="sj-feedback-file-input"
            onChange={this.handleFileInputChange}
          />
          <Button
            icon={<PictureOutlined />}
            onClick={() => this.fileInput && this.fileInput.click()}
            disabled={images.length >= MAX_IMAGE_COUNT}
          >
            添加图片
          </Button>
          <span className="sj-feedback-image-hint">支持粘贴剪贴板图片，最多 {MAX_IMAGE_COUNT} 张</span>
          {images.length > 0 && (
            <div className="sj-feedback-image-list">
              {images.map(image => (
                <div className="sj-feedback-image-item" key={image.id}>
                  <img src={image.previewUrl} alt={image.name} />
                  <button
                    type="button"
                    className="sj-feedback-image-remove"
                    aria-label="删除图片"
                    onClick={() => this.removeImage(image.id)}
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="sj-feedback-field">
          <div className="sj-feedback-label">联系方式（可选）</div>
          <Input
            value={contact}
            onChange={e => this.setState({ contact: e.target.value })}
            placeholder="手机号、微信或邮箱"
            maxLength={80}
          />
        </div>
        <div className="sj-feedback-note">提交时会自动附带当前页面、用户信息、浏览器环境和最近错误日志。</div>
        <Button
          type="primary"
          icon={<SendOutlined />}
          loading={submitting}
          onClick={this.submit}
          style={{ marginTop: 16 }}
        >
          提交反馈
        </Button>
      </div>
    );
  }
}
